/**
 * AccordionV2 component that renders a collapsible section with a header and content.
 *
 * @param children - The content to be displayed inside the accordion.
 * @param headerChildren - The content to be displayed in the header of the accordion.
 * @param className - Additional class names to apply to the accordion container.
 * @param arrowClassName - Additional class names to apply to the chevron icon.
 * @param testId - The data-testid attribute for the component, default is "accordion-component".
 *
 * @returns A React element representing the accordion component.
 */

import { PropsWithChildren } from "react";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Transition,
} from "@headlessui/react";
import { CommonClassNameAndTestId } from "@typings/Common";
import { cn } from "@util/helpers";

export type AccordionProps = {
  /** header component - header details in the accordion */
  headerChildren?: React.ReactNode;
  /** main classlist for the accordion*/
  className?: string;
  /** classlist for the chevron beside the header */
  arrowClassName?: string;
  /** Flag to check for chevron direction */
  isArrowRight?: boolean;
  /** child component - details in the accordion */
} & PropsWithChildren &
  /** common types - classname and test id*/
  CommonClassNameAndTestId;

/** Updated accodion component for myBT at myaccount , ge consituences slider, etc */
export default function AccordionV2({
  children,
  headerChildren,
  className,
  arrowClassName,
  isArrowRight,
  testId = "accordion-component",
}: AccordionProps): React.ReactElement {
  return (
    <div
      className={cn("border-b border-gray-175", className)}
      data-testid={testId}
    >
      <Disclosure as={"div"}>
        {({ open }) => (
          <>
            <DisclosureButton
              className={"flex w-full items-center justify-between"}
            >
              {headerChildren}

              <div
                className={cn(
                  "flex h-[42px] w-[42px] items-center justify-center rounded-full border border-gray-175 lg:border-none",
                  arrowClassName
                )}
              >
                <svg
                  className={cn(
                    "w-[15px] text-gray-850 transition-all duration-300 lg:text-gray-650",
                    { "rotate-180": open },
                    { "rotate-90": isArrowRight && open },
                    { "lg:text-gray-850": isArrowRight }
                  )}
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 12 8"
                  fill="none"
                >
                  <path
                    d="M1 1.33398C5 5.33398 6 6.33398 6 6.33398L11 1.33398"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              </div>
            </DisclosureButton>

            <Transition
              as={"div"}
              show={open}
              className="overflow-hidden transition-all duration-500"
              enterFrom="opacity-0 max-h-0"
              enterTo="opacity-100 max-h-96"
              leaveFrom="opacity-100 max-h-96"
              leaveTo="opacity-0 max-h-0"
            >
              <DisclosurePanel as={"div"}>{children}</DisclosurePanel>
            </Transition>
          </>
        )}
      </Disclosure>
    </div>
  );
}
